/**
 * Admin — tarifs et promos (migrations 0304, 0717), `/tutor/admin/skillcards-pricing`.
 *   • Promos SKILLCARDS (remise en % entre deux dates) et réglages des
 *     abonnements (essai gratuit, Pass 24 h et abonnement 7 jours proposés ou non).
 *
 * La grille est en BASE : la modifier change immédiatement les prix affichés
 * ET facturés (le checkout envoie à Stripe le montant calculé par
 * `skillcard_quote`, aucun Prix Stripe à maintenir).
 *   • Réglages : prix unitaire, paliers de remise des packs, remise programme,
 *     pas d'arrondi, durée du deck, questions gratuites.
 *   • Prix forcé d'un programme / d'un pack (vide = grille automatique).
 *   • Derniers achats.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Plus, Save, Trash2, Tags } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import {
  formatEuros,
  previewSkillcardPrice,
  validatePackTiers,
  type SkillcardGridPack,
  type SkillcardGridProgramme,
  type SkillcardPackTier,
  type SkillcardPricingSettings,
} from '@jeraisonne/db/cards';
import {
  centsToEurosInput,
  parseEurosToCents,
  useRecentSkillcardPurchases,
  useSaveSkillcardPricing,
  useSetPriceOverride,
  useSkillcardPricingSettings,
} from '@/lib/admin/skillcard-pricing';
import { useSkillcardPriceGrid } from '@/lib/skillcards/purchase';
import { OfferSettingsPanel } from '@/components/admin/offer-settings-panel';
import { PromotionsPanel } from '@/components/admin/promotions-panel';

const inputCls =
  'border-ink-200 text-ink-900 focus:ring-primary-400 w-full min-h-[2.75rem] rounded-xl border bg-white px-3 font-sans text-sm focus:outline-none focus:ring-2';
const labelCls = 'font-display text-ink-700 text-xs font-semibold';

interface Draft {
  unit: string;
  tiers: { min: string; pct: string }[];
  programmePct: string;
  /** Plafond du prix d'un programme (€), vide = aucun (migration 0507). */
  programmeMax: string;
  roundStep: string;
  validityMonths: string;
  freeQuestions: string;
}

function toDraft(s: SkillcardPricingSettings): Draft {
  return {
    unit: centsToEurosInput(s.unit_price_cents),
    tiers: s.pack_tiers.map((t) => ({ min: String(t.min_cards), pct: String(t.discount_pct) })),
    programmePct: String(s.programme_discount_pct),
    programmeMax: s.programme_max_cents != null ? centsToEurosInput(s.programme_max_cents) : '',
    roundStep: String(s.round_step_cents),
    validityMonths: String(s.validity_months),
    freeQuestions: String(s.free_sample_questions),
  };
}

function fromDraft(d: Draft): { settings: SkillcardPricingSettings | null; error: string | null } {
  const unit = parseEurosToCents(d.unit);
  if (unit == null || Number.isNaN(unit) || unit < 50) {
    return { settings: null, error: 'Prix unitaire invalide (minimum 0,50 €).' };
  }
  const tiers: SkillcardPackTier[] = d.tiers.map((t) => ({
    min_cards: Number(t.min),
    discount_pct: Number(t.pct.replace(',', '.')),
  }));
  const tierErr = validatePackTiers(tiers);
  if (tierErr) return { settings: null, error: tierErr };
  const programmePct = Number(d.programmePct.replace(',', '.'));
  if (!(programmePct >= 0 && programmePct <= 90)) {
    return { settings: null, error: 'Remise programme entre 0 et 90 %.' };
  }
  const programmeMax = d.programmeMax.trim() ? parseEurosToCents(d.programmeMax) : null;
  if (programmeMax !== null && (Number.isNaN(programmeMax) || programmeMax < 50)) {
    return { settings: null, error: 'Plafond programme invalide (minimum 0,50 €, ou vide).' };
  }
  const roundStep = Number(d.roundStep);
  const validity = Number(d.validityMonths);
  const freeQ = Number(d.freeQuestions);
  if (!Number.isInteger(roundStep) || roundStep < 1 || roundStep > 100) {
    return { settings: null, error: 'Arrondi entre 1 et 100 centimes.' };
  }
  if (!Number.isInteger(validity) || validity < 1 || validity > 36) {
    return { settings: null, error: 'Durée du deck entre 1 et 36 mois.' };
  }
  if (!Number.isInteger(freeQ) || freeQ < 1 || freeQ > 20) {
    return { settings: null, error: 'Questions gratuites entre 1 et 20.' };
  }
  return {
    settings: {
      unit_price_cents: unit,
      pack_tiers: [...tiers].sort((a, b) => a.min_cards - b.min_cards),
      programme_discount_pct: programmePct,
      programme_max_cents: programmeMax,
      round_step_cents: roundStep,
      validity_months: validity,
      free_sample_questions: freeQ,
    },
    error: null,
  };
}

export function TutorAdminSkillcardPricingPage() {
  const settings = useSkillcardPricingSettings();
  const save = useSaveSkillcardPricing();
  const grid = useSkillcardPriceGrid();
  const purchases = useRecentSkillcardPurchases();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings.data && !draft) setDraft(toDraft(settings.data));
  }, [settings.data, draft]);

  if (settings.isLoading || !draft) {
    return <div className="bg-ink-100 m-4 h-40 animate-pulse rounded-2xl" />;
  }
  if (settings.error) {
    return <p className="text-error-600 p-4 text-sm">Impossible de charger la grille.</p>;
  }

  const parsed = fromDraft(draft);
  const preview = parsed.settings;

  async function onSave() {
    if (!parsed.settings) return;
    setSaved(false);
    await save.mutateAsync(parsed.settings);
    setSaved(true);
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] sm:py-8">
      <div>
        <h1 className="font-display text-ink-900 flex items-center gap-2 text-2xl font-extrabold">
          <Tags className="h-6 w-6" aria-hidden /> Tarifs et promos
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Les prix sont calculés par la base et envoyés tels quels à Stripe : aucun prix à créer
          dans Stripe. Une modification s’applique immédiatement. Les abonnements et pass incluent
          déjà toutes les SKILLCARDS.
        </p>
      </div>

      <PromotionsPanel unitPriceCents={settings.data?.unit_price_cents ?? null} />
      <OfferSettingsPanel />

      <section className="border-ink-200 space-y-4 rounded-2xl border bg-white p-4 sm:p-5">
        <h2 className="font-display text-ink-900 text-lg font-bold">Grille des SKILLCARDS</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Prix d’une carte (€)">
            <input
              className={inputCls}
              inputMode="decimal"
              value={draft.unit}
              onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
            />
          </Field>
          <Field label="Remise programme (%)">
            <input
              className={inputCls}
              inputMode="decimal"
              value={draft.programmePct}
              onChange={(e) => setDraft({ ...draft, programmePct: e.target.value })}
            />
          </Field>
          <Field label="Plafond d’un programme (€, vide = aucun)">
            <input
              className={inputCls}
              inputMode="decimal"
              placeholder="79,90"
              value={draft.programmeMax}
              onChange={(e) => setDraft({ ...draft, programmeMax: e.target.value })}
            />
          </Field>
          <Field label="Arrondi (centimes)">
            <input
              className={inputCls}
              inputMode="numeric"
              value={draft.roundStep}
              onChange={(e) => setDraft({ ...draft, roundStep: e.target.value })}
            />
          </Field>
          <Field label="Durée du deck (mois)">
            <input
              className={inputCls}
              inputMode="numeric"
              value={draft.validityMonths}
              onChange={(e) => setDraft({ ...draft, validityMonths: e.target.value })}
            />
          </Field>
          <Field label="Questions gratuites (exercice non découpé)">
            <input
              className={inputCls}
              inputMode="numeric"
              value={draft.freeQuestions}
              onChange={(e) => setDraft({ ...draft, freeQuestions: e.target.value })}
            />
          </Field>
        </div>

        <div className="space-y-2">
          <p className={labelCls}>Remise des packs selon le nombre de cartes</p>
          {draft.tiers.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-ink-600 shrink-0 text-sm">Dès</span>
              <input
                aria-label={`Palier ${i + 1} : nombre de cartes`}
                className={cn(inputCls, 'w-20')}
                inputMode="numeric"
                value={t.min}
                onChange={(e) => {
                  const tiers = [...draft.tiers];
                  tiers[i] = { ...t, min: e.target.value };
                  setDraft({ ...draft, tiers });
                }}
              />
              <span className="text-ink-600 shrink-0 text-sm">cartes :</span>
              <input
                aria-label={`Palier ${i + 1} : remise en %`}
                className={cn(inputCls, 'w-20')}
                inputMode="decimal"
                value={t.pct}
                onChange={(e) => {
                  const tiers = [...draft.tiers];
                  tiers[i] = { ...t, pct: e.target.value };
                  setDraft({ ...draft, tiers });
                }}
              />
              <span className="text-ink-600 shrink-0 text-sm">%</span>
              <button
                type="button"
                aria-label={`Supprimer le palier ${i + 1}`}
                onClick={() => setDraft({ ...draft, tiers: draft.tiers.filter((_, j) => j !== i) })}
                className="text-ink-400 hover:text-error-600 flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setDraft({ ...draft, tiers: [...draft.tiers, { min: '', pct: '' }] })}
            className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm font-semibold"
          >
            <Plus className="h-4 w-4" aria-hidden /> Ajouter un palier
          </button>
        </div>

        {preview && (
          <div className="bg-ink-50 rounded-xl p-3 text-sm">
            <p className={labelCls}>Aperçu</p>
            <ul className="text-ink-700 mt-1 grid grid-cols-1 gap-1 sm:grid-cols-2">
              {[1, 3, 5, 8, 12].map((n) => {
                const price = previewSkillcardPrice(preview, n === 1 ? 'card' : 'pack', n);
                return (
                  <li key={n}>
                    {n === 1 ? '1 carte' : `Pack de ${n}`} : <strong>{formatEuros(price)}</strong>
                    {n > 1 && <> ({formatEuros(Math.round(price / n))}/carte)</>}
                  </li>
                );
              })}
              {[40, 85].map((n) => {
                const price = previewSkillcardPrice(preview, 'programme', n);
                return (
                  <li key={`p${n}`}>
                    Programme de {n} cartes : <strong>{formatEuros(price)}</strong> (
                    {formatEuros(Math.round(price / n))}/carte)
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {parsed.error && (
          <p role="alert" className="text-error-600 text-sm">
            {parsed.error}
          </p>
        )}
        {save.error && (
          <p role="alert" className="text-error-600 text-sm">
            Enregistrement impossible : {(save.error as Error).message}
          </p>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button
            onClick={() => void onSave()}
            disabled={!parsed.settings || save.isPending}
            className="w-full sm:w-auto"
          >
            <Save className="mr-1.5 h-4 w-4" aria-hidden />
            {save.isPending ? 'Enregistrement…' : 'Enregistrer la grille'}
          </Button>
          {saved && <p className="text-success-700 text-sm">Grille enregistrée.</p>}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="font-display text-ink-900 text-lg font-bold">Programmes et packs</h2>
        <p className="text-ink-500 text-sm">
          Prix catalogue (aucune carte possédée). Un prix forcé remplace la grille pour le lot
          complet ; un élève qui possède déjà des cartes paie au prorata.
        </p>
        {grid.isLoading ? (
          <div className="bg-ink-100 h-24 animate-pulse rounded-2xl" />
        ) : (
          (grid.data?.programmes ?? []).map((p) => <ProgrammeRow key={p.target_id} programme={p} />)
        )}
      </section>

      <section className="space-y-2">
        <h2 className="font-display text-ink-900 text-lg font-bold">Derniers achats</h2>
        {(purchases.data ?? []).length === 0 ? (
          <p className="text-ink-500 text-sm">Aucun achat pour le moment.</p>
        ) : (
          <ul className="divide-ink-100 border-ink-200 divide-y rounded-2xl border bg-white">
            {(purchases.data ?? []).map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 p-3 text-sm">
                <span className="text-ink-900 min-w-0 flex-1 truncate font-semibold">
                  {p.label}
                </span>
                <span className="text-ink-500">
                  {p.kind === 'card' ? 'Carte' : p.kind === 'pack' ? 'Pack' : 'Programme'} ·{' '}
                  {p.card_count} carte{p.card_count > 1 ? 's' : ''}
                </span>
                <span className="font-semibold">{formatEuros(p.amount_cents)}</span>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-semibold',
                    p.status === 'paid'
                      ? 'bg-success-50 text-success-700'
                      : p.status === 'failed'
                        ? 'bg-error-50 text-error-700'
                        : 'bg-ink-100 text-ink-600',
                  )}
                >
                  {p.status === 'paid' ? 'Payé' : p.status === 'failed' ? 'Échec' : 'En attente'}
                </span>
                <span className="text-ink-400 text-xs">
                  {new Date(p.created_at).toLocaleString('fr-FR')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className={labelCls}>{label}</span>
      {children}
    </label>
  );
}

function ProgrammeRow({ programme }: { programme: SkillcardGridProgramme }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-ink-200 rounded-2xl border bg-white p-3 sm:p-4">
      <PriceLine
        kind="programme"
        id={programme.target_id}
        title={programme.title}
        totalCards={programme.total_cards}
        priceCents={programme.price_cents}
        savings={programme.savings_pct}
        overrideCents={programme.override_cents}
      />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-primary-600 mt-1 inline-flex min-h-[2.75rem] items-center text-sm font-semibold"
      >
        {open ? 'Masquer' : 'Voir'} les {programme.packs.length} packs
      </button>
      {open && (
        <div className="border-ink-100 mt-1 space-y-2 border-t pt-2">
          {programme.packs.map((k: SkillcardGridPack) => (
            <PriceLine
              key={k.target_id}
              kind="pack"
              id={k.target_id}
              title={k.label}
              totalCards={k.total_cards}
              priceCents={k.price_cents}
              savings={k.savings_pct}
              overrideCents={k.override_cents}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PriceLine({
  kind,
  id,
  title,
  totalCards,
  priceCents,
  savings,
  overrideCents,
}: {
  kind: 'pack' | 'programme';
  id: string;
  title: string;
  totalCards: number;
  priceCents: number;
  savings: number;
  overrideCents: number | null;
}) {
  const setOverride = useSetPriceOverride();
  const [value, setValue] = useState(centsToEurosInput(overrideCents));
  useEffect(() => setValue(centsToEurosInput(overrideCents)), [overrideCents]);
  const cents = parseEurosToCents(value);
  const invalid = cents !== null && (Number.isNaN(cents) || cents < 50);
  const dirty = cents !== overrideCents && !invalid;
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <p className="text-ink-900 text-sm font-semibold">{title}</p>
        <p className="text-ink-500 text-xs">
          {totalCards} cartes · <strong className="text-ink-800">{formatEuros(priceCents)}</strong>
          {savings > 0 && <> · −{savings} % vs à l’unité</>}
          {overrideCents != null && <> · prix forcé</>}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <input
          aria-label={`Prix forcé de ${title} (€), vide = grille`}
          placeholder="Grille"
          className={cn(inputCls, 'w-28', invalid && 'border-error-400')}
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <button
          type="button"
          disabled={!dirty || setOverride.isPending}
          onClick={() => setOverride.mutate({ kind, id, cents })}
          className="bg-primary-500 min-h-[2.75rem] rounded-xl px-3 text-sm font-semibold text-white active:scale-[0.98] disabled:opacity-40"
        >
          OK
        </button>
      </div>
    </div>
  );
}
